import React, { useEffect, useState } from 'react';
import './CommonStyles.css';
import './ViewProductModal.css';
import apiService from '../../services/api';

const ViewStockAdjustmentModal = ({ adjustment, onClose }) => {
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDetails = async () => {
            if (!adjustment?.id) return;
            try {
                setLoading(true);
                if (!adjustment.stock_adjustment_lines) {
                    const data = await apiService.getStockAdjustmentDetails(adjustment.id);
                    setDetails(data.data || data || adjustment);
                } else {
                    setDetails(adjustment);
                }
            } catch (err) {
                console.error('Failed to fetch adjustment details', err);
                setDetails(adjustment);
            } finally {
                setLoading(false);
            }
        };
        fetchDetails();
    }, [adjustment]);

    if (!adjustment) return null;

    const formatCurrency = (amount) => `$ ${parseFloat(amount || 0).toFixed(2)}`;

    const data = details || adjustment;
    const products = data.stock_adjustment_lines || data.products || [];
    const locationName = data.location?.name || data.location_name || data.business_location?.name || '-';
    const adjType = data.adjustment_type || '-';

    return (
        <div className="modal-overlay">
            <div className="modal-container view-product-modal printable-modal">
                {/* Header */}
                <div className="modal-header">
                    <h2>Stock Adjustment Details</h2>
                    <button className="modal-close" onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="modal-body printable-content">
                    {loading ? (
                        <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                            <div className="loading-spinner"></div>
                            <p style={{ marginTop: '1rem' }}>Loading details...</p>
                        </div>
                    ) : (
                        <div className="product-details-grid">
                            {/* Row 1: Ref No + Date */}
                            <div className="detail-row">
                                <div className="detail-item">
                                    <p className="detail-label">Reference No</p>
                                    <p className="detail-value" style={{ fontWeight: '700', color: '#1e293b' }}>{data.ref_no || data.refNo || '-'}</p>
                                </div>
                                <div className="detail-item">
                                    <p className="detail-label">Date</p>
                                    <p className="detail-value">{data.transaction_date ? data.transaction_date.slice(0, 16).replace('T', ' ') : '-'}</p>
                                </div>
                                <div className="detail-item">
                                    <p className="detail-label">Business Location</p>
                                    <p className="detail-value" style={{ fontWeight: '600' }}>{locationName}</p>
                                </div>
                            </div>

                            {/* Row 2: Adjustment Type + Recovered + Total */}
                            <div className="detail-row">
                                <div className="detail-item">
                                    <p className="detail-label">Adjustment Type</p>
                                    <span style={{
                                        display: 'inline-block',
                                        marginTop: '0.25rem',
                                        padding: '0.2rem 0.75rem',
                                        borderRadius: '9999px',
                                        fontSize: '0.7rem',
                                        fontWeight: '800',
                                        textTransform: 'uppercase',
                                        background: adjType === 'abnormal' ? '#fee2e2' : '#f0fdf4',
                                        color: adjType === 'abnormal' ? '#b91c1c' : '#15803d',
                                    }}>
                                        {adjType}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <p className="detail-label">Amount Recovered</p>
                                    <p className="detail-value">{formatCurrency(data.total_amount_recovered || data.amountRecovered)}</p>
                                </div>
                                <div className="detail-item">
                                    <p className="detail-label">Total Amount</p>
                                    <p className="detail-value" style={{ fontWeight: '700', color: '#2563eb' }}>{formatCurrency(data.final_total || data.totalAmount)}</p>
                                </div>
                            </div>

                            {/* Notes */}
                            {(data.additional_notes || data.reason) && (
                                <div className="detail-row">
                                    <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                                        <p className="detail-label">Reason / Notes</p>
                                        <p className="detail-value" style={{ fontStyle: 'italic' }}>{data.additional_notes || data.reason}</p>
                                    </div>
                                </div>
                            )}

                            {/* Products Table */}
                            <div style={{ marginTop: '0.5rem' }}>
                                <p style={{ fontSize: '0.95rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.75rem' }}>
                                    Adjusted Products ({products.length})
                                </p>
                                <div className="pricing-table">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Product</th>
                                                <th>Quantity</th>
                                                <th>Unit Price</th>
                                                <th>Subtotal</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {products.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                                                        No products found.
                                                    </td>
                                                </tr>
                                            ) : products.map((p, idx) => {
                                                const name = p.variation?.product?.name || p.product?.name || p.name || 'Unknown Product';
                                                const sku  = p.variation?.product?.sku  || p.product?.sku  || p.sku  || '--';
                                                const qty  = parseFloat(p.quantity || 0);
                                                const price = parseFloat(p.unit_price || p.purchase_price || 0);
                                                const sub   = p.subtotal ? parseFloat(p.subtotal) : qty * price;
                                                return (
                                                    <tr key={idx}>
                                                        <td>{idx + 1}</td>
                                                        <td>
                                                            <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>{name}</div>
                                                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>SKU: {sku}</div>
                                                        </td>
                                                        <td>{qty.toFixed(4)}</td>
                                                        <td>{formatCurrency(price)}</td>
                                                        <td style={{ fontWeight: '600' }}>{formatCurrency(sub)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="modal-footer">
                    <button className="btn-close-modal" onClick={onClose}>Close</button>
                    <button className="btn-print" onClick={() => window.print()}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                            <rect x="6" y="14" width="12" height="8"/>
                        </svg>
                        Print
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ViewStockAdjustmentModal;
